// src/components/admin/CategoryManagement.jsx
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [subcategories, setSubcategories] = useState([{ name: '', disabled: false }]);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await fetch('http://localhost:5003/admin/categories');
      const data = await res.json();
      setCategories(data);
    } catch (err) {
      toast.error('Failed to fetch categories');
    }
  };

  const handleAddSubcategory = () => {
    setSubcategories([...subcategories, { name: '', disabled: false }]);
  };

  const handleSubcategoryChange = (value, index) => {
    const updated = [...subcategories];
    updated[index].name = value;
    setSubcategories(updated);
  };

  const handleToggleSubcategory = (index) => {
    const updated = [...subcategories];
    updated[index].disabled = !updated[index].disabled;
    setSubcategories(updated);
  };

  const handleRemoveSubcategory = (index) => {
    const updated = [...subcategories];
    updated.splice(index, 1);
    setSubcategories(updated);
  };

  const handleCreate = async () => {
    if (!newCategory.trim()) {
      toast.error('Category name is required');
      return;
    }
    try {
      const res = await fetch('http://localhost:5003/admin/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCategory, subcategories })
      });
      if (res.ok) {
        toast.success('Category created');
        setNewCategory('');
        setSubcategories([{ name: '', disabled: false }]);
        fetchCategories();
      } else {
        toast.error('Failed to create category');
      }
    } catch (err) {
      toast.error('Error while creating category');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      const res = await fetch(`http://localhost:5003/admin/categories/${id}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Category deleted');
        fetchCategories();
      } else {
        toast.error('Failed to delete category');
      }
    } catch (err) {
      toast.error('Error deleting category');
    }
  };

  const toggleDisable = async (id, disabled) => {
    try {
      const res = await fetch(`http://localhost:5003/admin/categories/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ disabled: !disabled })
      });
      if (res.ok) {
        toast.success(`Category ${!disabled ? 'disabled' : 'enabled'}`);
        fetchCategories();
      } else {
        toast.error('Failed to update status');
      }
    } catch (err) {
      toast.error('Error updating category');
    }
  };

  const handleEdit = (cat) => {
    setEditingId(cat._id);
    setNewCategory(cat.name);
    setSubcategories(
      cat.subcategories.map(sub =>
        typeof sub === 'string' ? { name: sub, disabled: false } : sub
      )
    );
  };

  const handleUpdate = async () => {
    if (!newCategory.trim()) {
      toast.error('Category name is required');
      return;
    }
    try {
      const res = await fetch(`http://localhost:5003/admin/categories/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCategory, subcategories })
      });
      if (res.ok) {
        toast.success('Category updated');
        setNewCategory('');
        setSubcategories([{ name: '', disabled: false }]);
        setEditingId(null);
        fetchCategories();
      } else {
        toast.error('Failed to update category');
      }
    } catch (err) {
      toast.error('Error while updating category');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border p-4 rounded-md shadow space-y-4">
        <h3 className="text-lg font-semibold">
          {editingId ? 'Edit Category' : 'Add New Category'}
        </h3>
        <input
          type="text"
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          placeholder="Category name"
          className="border px-3 py-2 rounded w-full"
        />

        <div className="space-y-2">
          {subcategories.map((sub, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                value={sub.name}
                onChange={(e) => handleSubcategoryChange(e.target.value, idx)}
                placeholder={`Subcategory ${idx + 1}`}
                className="border px-3 py-2 rounded w-full"
              />
              <label className="flex items-center text-xs">
                <input
                  type="checkbox"
                  checked={sub.disabled}
                  onChange={() => handleToggleSubcategory(idx)}
                  className="mr-1"
                />
                Disabled
              </label>
              <button
                onClick={() => handleRemoveSubcategory(idx)}
                className="text-red-500 text-sm hover:underline"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            onClick={handleAddSubcategory}
            className="text-blue-600 text-sm hover:underline"
          >
            + Add Subcategory
          </button>
        </div>

        <button
          onClick={editingId ? handleUpdate : handleCreate}
          className="bg-blue-600 text-white px-4 py-2 rounded-md"
        >
          {editingId ? 'Update Category' : 'Create Category'}
        </button>
      </div>

      <div className="bg-white border rounded shadow p-4">
        <h3 className="text-lg font-semibold mb-2">Existing Categories</h3>
        <ul className="space-y-4">
          {categories.map(cat => (
            <li key={cat._id} className="border-b pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <strong>{cat.name}</strong>{' '}
                  {cat.disabled ? (
                    <span className="text-xs text-red-600 ml-2">(Disabled)</span>
                  ) : (
                    <span className="text-xs text-green-600 ml-2">(Enabled)</span>
                  )}
                  <ul className="text-sm text-gray-600 list-disc ml-6">
                    {cat.subcategories.map((sub, i) => (
                      <li key={i}>
                        {typeof sub === 'string' ? sub : sub.name}{' '}
                        {typeof sub !== 'string' && sub.disabled && (
                          <span className="text-red-500 text-xs ml-1">(Disabled)</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(cat)}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => toggleDisable(cat._id, cat.disabled)}
                    className="text-xs text-yellow-600 hover:underline"
                  >
                    {cat.disabled ? 'Enable' : 'Disable'}
                  </button>
                  <button
                    onClick={() => handleDelete(cat._id)}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default CategoryManagement;
